import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dashboardService from '../common/services/next-gen/DashboardService';

/**
 * Hook to fetch user dashboard
 */
export function useUserDashboard() {
    return useQuery({
        queryKey: ['userDashboard'],
        queryFn: dashboardService.getUserDashboard,
        staleTime: 60000 // 1 minute
    });
}

/**
 * Hook to fetch available widget definitions
 * Only fetches user-category widgets (not system widgets)
 */
export function useWidgetDefinitions() {
    return useQuery({
        queryKey: ['widgetDefinitions'],
        queryFn: () => dashboardService.getWidgetDefinitions('user'),
        staleTime: 600000 // 10 minutes
    });
}

/**
 * Hook to update dashboard layout (drag-and-drop)
 */
export function useUpdateDashboardLayout() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request) => dashboardService.updateDashboardLayout(request),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to add a widget
 */
export function useAddWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request) => dashboardService.addWidget(request),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to update a widget
 */
export function useUpdateWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userDashboardWidgetId, request }) => 
            dashboardService.updateWidget(userDashboardWidgetId, request),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to remove a widget
 */
export function useRemoveWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userDashboardWidgetId) => dashboardService.removeWidget(userDashboardWidgetId),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to save widget filters
 */
export function useSaveWidgetFilters() {
    return useMutation({
        mutationFn: ({ userDashboardWidgetId, filterConfig }) => 
            dashboardService.saveWidgetFilters(userDashboardWidgetId, filterConfig)
    });
}

/**
 * Hook to toggle collapsed state
 */
export function useToggleCollapsed() {
    return useMutation({
        mutationFn: ({ userDashboardWidgetId, isCollapsed }) => 
            dashboardService.toggleCollapsed(userDashboardWidgetId, isCollapsed)
    });
}
